import { useState, useEffect, useRef } from 'react'
import { articulosApi } from '../api/notasVenta'
import { fotosIaApi } from '../api/fotosIa'
import CorreccionFotoIa from './CorreccionFotoIa'
import Boton from './Boton'
import Modal from './Modal'
import { colors, text, space, radius, sx } from '../design/tokens'

// ─── Fotos del artículo (ficha /articulos/:id) ──────────────────────────────
// La principal ({SKU}.jpg) + las adicionales ({SKU}_2.jpg, _3…) que la tienda
// muestra como galería en la ficha del producto (sugerencia #84). Cada foto
// que se sube se mide: si no tiene fondo blanco y las fotos con IA están
// prendidas, se pregunta si se pasa por Gemini (sugerencia #85, el mismo
// circuito de /m/fotos). Lo que devuelve la IA se acepta o se descarta: nada
// se publica solo. Se pueden subir varias a la vez: van de a una, en cola.

const fmtUsd = (n) => `US$${Number(n).toFixed(2).replace('.', ',')}`

export default function FotosArticuloCard({ articuloId, sku, descripcion, foto, onCambio }) {
  const colaRef = useRef([])                      // [{file, destino}] esperando turno
  const actualRef = useRef(null)
  const [pendientes, setPendientes] = useState(0)
  const [actual, setActual] = useState(null)      // {file, destino} en proceso
  const [decision, setDecision] = useState(null)  // {usd} → el fondo no es blanco: ¿IA o así?
  const [revision, setRevision] = useState(null)  // {id, destino, src} → resultado de la IA para aceptar
  const [trabajando, setTrabajando] = useState('') // texto del paso en curso
  // El error se muestra EN la tarjeta: el banner de la ficha queda arriba, fuera de la vista.
  const [error, setError] = useState('')
  const onError = setError
  const principalRef = useRef(null)
  const extraRef = useRef(null)

  const extras = foto?.extras ?? []
  const hayPrincipal = !!foto?.existe
  const ocupado = !!actual || !!trabajando

  // Funciones comunes (no useCallback): la cola las encadena entre sí.
  async function subir(item, ia = null) {
    setError('')
    setTrabajando(ia === 'si' ? 'La IA está armando la foto de estudio… (unos segundos)' : 'Subiendo…')
    try {
      const { data } = await articulosApi.subirFoto(articuloId, item.file, { destino: item.destino, ia })
      if (data.decision === 'fondo') {
        setTrabajando(''); setDecision({ usd: data.usd })
        return
      }
      if (data.foto_ia) {
        const blob = await fotosIaApi.imagen(data.foto_ia.id)
        setTrabajando(''); setDecision(null)
        setRevision({ id: data.foto_ia.id, destino: item.destino, fondoOk: data.foto_ia.fondo_ok !== false, src: URL.createObjectURL(blob.data) })
        return
      }
      onCambio()
    } catch (e) {
      onError(e.response?.data?.message || e.response?.data?.errors?.foto?.[0] || 'No se pudo subir la foto.')
    }
    terminar()
  }

  // Cola: de a una. Cuando una termina (subida, aceptada, descartada o
  // cancelada) arranca la siguiente.
  function siguiente() {
    const sig = colaRef.current.shift() || null
    setPendientes(colaRef.current.length)
    actualRef.current = sig
    setActual(sig)
    if (sig) subir(sig)
  }

  function terminar() {
    setDecision(null); setRevision(null); setTrabajando('')
    siguiente()
  }

  useEffect(() => () => { if (revision?.src) URL.revokeObjectURL(revision.src) }, [revision])

  const encolar = (files, destino) => {
    const lista = Array.from(files || []).map(file => ({ file, destino }))
    if (!lista.length) return
    colaRef.current.push(...lista)
    setPendientes(colaRef.current.length)
    if (!actualRef.current) siguiente()
  }

  const aceptarIa = async () => {
    setTrabajando('Publicando…')
    try {
      await fotosIaApi.publicar(revision.id, revision.destino)
      onCambio()
    } catch (e) {
      onError(e.response?.data?.message || 'No se pudo publicar la foto.')
    }
    terminar()
  }

  const descartarIa = async () => {
    try { await fotosIaApi.descartar(revision.id) } catch { /* queda procesada: no se publica igual */ }
    terminar()
  }

  const accion = async (fn, msjError) => {
    setError(''); setTrabajando('Guardando…')
    try { await fn(); onCambio() } catch (e) { onError(e.response?.data?.message || msjError) }
    setTrabajando('')
  }

  const quitarPrincipal = () => {
    const aviso = extras.length
      ? '¿Quitar la foto principal? La primera de "Más fotos" pasa a ser la principal.'
      : '¿Quitar la foto? La web deja de mostrarla.'
    if (window.confirm(aviso)) accion(() => articulosApi.borrarFoto(articuloId), 'No se pudo quitar la foto.')
  }
  const quitarExtra = (n) => {
    if (window.confirm('¿Quitar esta foto? La web deja de mostrarla en la galería.')) {
      accion(() => articulosApi.borrarFoto(articuloId, n), 'No se pudo quitar la foto.')
    }
  }
  const hacerPrincipal = (n) => accion(() => articulosApi.hacerFotoPrincipal(articuloId, n), 'No se pudo cambiar la principal.')

  const nombreActual = actual?.file?.name

  return (
    <div style={st.card}>
      <h3 style={sx.cardTitle}>Foto</h3>
      <div style={st.fotoBox}>
        {hayPrincipal
          ? <img key={foto.url} src={foto.url} alt={descripcion} style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }} />
          : <div style={st.placeholder}>Sin foto</div>}
      </div>
      <div style={{ display: 'flex', gap: space.sm, marginTop: space.md }}>
        <input ref={principalRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
          onChange={e => { encolar(e.target.files, 'principal'); e.target.value = '' }} />
        <Boton variante="secondary" sm disabled={ocupado} onClick={() => principalRef.current?.click()}>
          {hayPrincipal ? 'Reemplazar' : '⬆ Subir foto'}
        </Boton>
        {hayPrincipal && <Boton variante="destructive" sm disabled={ocupado} onClick={quitarPrincipal}>Quitar</Boton>}
      </div>

      {/* Galería: lo que la tienda muestra como miniaturas en la ficha del producto */}
      <div style={{ marginTop: space.xl }}>
        <div style={st.subtitulo}>Más fotos{extras.length > 0 && <span style={st.contador}> · {extras.length}</span>}</div>
        {extras.length > 0 && (
          <div style={st.grilla}>
            {extras.map(x => (
              <div key={x.n} style={st.miniatura}>
                <img src={x.url} alt={`${descripcion} — foto ${x.n}`} style={st.miniImg} />
                <div style={st.miniAcciones}>
                  <Boton variante="secondary" sm style={st.miniBtn} disabled={ocupado} title="Usar como foto principal" onClick={() => hacerPrincipal(x.n)}>★</Boton>
                  <Boton variante="destructive" sm style={st.miniBtn} disabled={ocupado} title="Quitar esta foto" onClick={() => quitarExtra(x.n)}>✕</Boton>
                </div>
              </div>
            ))}
          </div>
        )}
        <input ref={extraRef} type="file" multiple accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
          onChange={e => { encolar(e.target.files, 'extra'); e.target.value = '' }} />
        <div style={{ marginTop: space.md }}>
          <Boton variante="secondary" sm disabled={ocupado || !hayPrincipal} onClick={() => extraRef.current?.click()}>＋ Agregar fotos</Boton>
        </div>
        {!hayPrincipal && <p style={st.nota}>Primero subí la foto principal: sin ella la web no muestra la galería.</p>}
      </div>

      {error && <div style={st.error}>{error}</div>}

      {trabajando && (
        <p style={{ ...st.nota, color: colors.textSec }}>
          {trabajando}{nombreActual && trabajando !== 'Guardando…' ? ` (${nombreActual})` : ''}{pendientes > 0 ? ` · ${pendientes} en espera` : ''}
        </p>
      )}

      <p style={st.nota}>
        Se guardan como {sku}.jpg (principal) y {sku}_2.jpg, _3… (galería) — las mismas que muestra ligierweb.
        Se ajustan solas al formato del catálogo (1000×1000, producto centrado).
        {foto?.ia_activa && ' Si una foto no tiene fondo blanco, se ofrece pasarla por la IA.'}
      </p>

      {decision && (
        <Modal title="La foto no tiene fondo blanco" onClose={terminar} maxWidth="520px">
          <p style={st.texto}>
            <b>{nombreActual}</b> tiene fondo de color o un ambiente. La IA puede convertirla en una foto de estudio
            (fondo blanco, de frente, la etiqueta tal cual). Cuesta {fmtUsd(decision.usd)} y antes de publicarla la vas a ver.
          </p>
          <div style={{ display: 'flex', gap: space.sm, justifyContent: 'flex-end', marginTop: space.xl, flexWrap: 'wrap' }}>
            <Boton variante="ghost" onClick={terminar}>Cancelar</Boton>
            <Boton variante="secondary" onClick={() => { setDecision(null); subir(actual, 'no') }}>Subirla así</Boton>
            <Boton variante="primary" onClick={() => { setDecision(null); subir(actual, 'si') }}>✨ Pasarla por la IA</Boton>
          </div>
        </Modal>
      )}

      {revision && (
        <Modal title="Resultado de la IA" onClose={descartarIa} maxWidth="560px">
          <div style={{ ...st.fotoBox, marginTop: 0, minHeight: 320 }}>
            <img src={revision.src} alt="Foto generada por la IA" style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }} />
          </div>
          {revision.correccion && <p style={st.texto}>Corrección pedida: «{revision.correccion}»</p>}
          {revision.fondoOk ? (
            <p style={st.texto}>
              Revisá que la etiqueta diga lo mismo que la botella real. Si la aceptás, queda como
              {revision.destino === 'extra' ? ' una foto más de la galería.' : ' la foto principal.'}
            </p>
          ) : (
            <p style={{ ...st.texto, color: colors.warn, fontWeight: 600 }}>
              La IA devolvió el fondo NO blanco: así no se puede usar. Pedí la corrección (ej. "fondo blanco puro") o descartala.
            </p>
          )}
          <CorreccionFotoIa
            fotoId={revision.id}
            disabled={!!trabajando}
            onCorregida={async (nueva) => {
              const blob = await fotosIaApi.imagen(nueva.id)
              setRevision(r => ({ ...r, id: nueva.id, fondoOk: nueva.fondo_ok !== false, correccion: nueva.correccion, src: URL.createObjectURL(blob.data) }))
            }}
          />
          <div style={{ display: 'flex', gap: space.sm, justifyContent: 'flex-end', marginTop: space.lg }}>
            <Boton variante="destructive" disabled={!!trabajando} onClick={descartarIa}>✕ Descartar</Boton>
            {revision.fondoOk && <Boton variante="success" disabled={!!trabajando} onClick={aceptarIa}>{trabajando || '✓ Usar esta foto'}</Boton>}
          </div>
        </Modal>
      )}
    </div>
  )
}

const st = {
  card: { ...sx.card },
  fotoBox: { marginTop: space.md, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.bgSoft, borderRadius: radius.sm, overflow: 'hidden' },
  placeholder: { color: colors.textPlh, fontSize: text.sec, padding: space.xl, textAlign: 'center' },
  subtitulo: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  contador: { color: colors.textMeta, fontWeight: 400 },
  grilla: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: space.sm, marginTop: space.md },
  miniatura: { position: 'relative', aspectRatio: '1 / 1', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.bg, overflow: 'hidden' },
  miniImg: { width: '100%', height: '100%', objectFit: 'contain' },
  miniAcciones: { position: 'absolute', top: space.xs, right: space.xs, display: 'flex', gap: 2 },
  // Solo el tamaño: la miniatura es chica (el aspecto lo pone la variante de Boton).
  miniBtn: { padding: '0 6px', lineHeight: '20px' },
  nota: { fontSize: text.meta, color: colors.textMeta, marginTop: space.sm, marginBottom: 0 },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px`, marginTop: space.md, fontSize: text.sec },
  texto: { fontSize: text.body, color: colors.text, lineHeight: 1.5, marginTop: space.md, marginBottom: 0 },
}
